import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, Table, Tag, Space, Statistic, Row, Col, message, Tabs, DatePicker, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, CopyOutlined, TrophyOutlined, DollarOutlined, SwapOutlined, StarOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import 'dayjs/locale/zh-cn';

// 配置dayjs插件
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.locale('zh-cn');

// 响应式工具函数
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenSize.width < 768;
  const isTablet = screenSize.width >= 768 && screenSize.width < 1024;
  const isDesktop = screenSize.width >= 1024;

  return { isMobile, isTablet, isDesktop, width: screenSize.width };
};

// 钱包数据接口
interface WalletData {
  id: string;
  address: string;
  remark: string;
  totalVolume: number; // USDT
  transactions: number;
  points: number;
  earnings: number; // 包含Gas费用
  nextLevelVolume: number; // 下一级所需交易量
  addTime: string;
}

// API接口数据类型
interface ApiResponse {
  data: {
    [address: string]: {
      address: string;
      date: string;
      pnl_by_token: { [token: string]: string } | {};
      total_buy_volume_usdt: string;
      total_gas_fees_bnb: string;
      total_gas_fees_usdt?: string; // 可能不存在
      total_received_by_token: { [token: string]: string } | {};
      total_sent_by_token: { [token: string]: string } | {};
      trade_count: number;
      transactions_list: Array<{
        block_number: number;
        dex_router: string;
        from_amount: string;
        from_token: string;
        gas_fee: string;
        status: number;
        timestamp: number;
        to_amount: string;
        to_token: string;
        trade_type: string;
        tx_hash: string;
      }> | [];
    };
  };
  metadata: {
    addresses_with_data: number;
    addresses_with_trades: number;
    date: string;
    total_addresses: number;
  };
  success: boolean;
}

// 积分计算逻辑
const calculatePoints = (volume: number): number => {
  if (volume < 2) return 0;
  
  // 找到最大的2的n次方小于等于volume
  let points = 0;
  let currentThreshold = 2;
  
  while (currentThreshold <= volume) {
    points++;
    currentThreshold *= 2;
  }
  // bsc 二倍
  return points + 1;
};

// 计算下一级所需交易量
const getNextLevelVolume = (currentVolume: number): number => {
  if (currentVolume < 2) return 2;
  
  let nextThreshold = 2;
  while (nextThreshold <= currentVolume) {
    nextThreshold *= 2;
  }
  
  return nextThreshold;
};

// Wei转USDT的转换函数
const weiToUsdt = (weiAmount: string | undefined): number => {
  // 处理undefined、null、空字符串的情况
  if (!weiAmount || weiAmount === '0' || weiAmount === '') {
    return 0;
  }
  
  try {
    // 假设18位小数
    const bn = BigInt(weiAmount);
    return Number(bn) / Math.pow(10, 18);
  } catch (error) {
    console.warn('Wei转换失败:', weiAmount, error);
    return 0;
  }
};

// 调用真实API获取交易数据
const fetchTradingData = async (addresses: string[], date: string): Promise<ApiResponse> => {
  try {
    const response = await fetch('https://new.alphabot.cm/api/v2/batch_daily_trading_report', {
      method: 'POST',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/137.0.0.0 Safari/537.36'
      },
      body: JSON.stringify({
        addresses,
        date,
      }),
      mode: 'cors',
    });

    if (!response.ok) {
      throw new Error(`API请求失败: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // 如果CORS失败，可以考虑使用代理或者显示提示
    if (error instanceof TypeError && error.message.includes('CORS')) {
      throw new Error('跨域请求被阻止，请检查网络设置或使用代理');
    }
    throw error;
  }
};

const BinanceAlpha: React.FC = () => {
  const responsive = useResponsive();
  const [wallets, setWallets] = useState<WalletData[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingWallet, setEditingWallet] = useState<WalletData | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    // 默认设置为今天日期
    return dayjs();
  });
  const [transactionDetailsVisible, setTransactionDetailsVisible] = useState(false);
  const [selectedWalletTransactions, setSelectedWalletTransactions] = useState<any[]>([]);

  // 响应式样式配置
  const getResponsiveStyles = () => {
    const { isMobile, isTablet } = responsive;
    
    return {
      heroHeight: isMobile ? '200px' : isTablet ? '220px' : '240px',
      heroPadding: isMobile ? '24px 16px' : isTablet ? '32px 20px' : '40px 24px',
      titleSize: isMobile ? '1.8rem' : isTablet ? '2.2rem' : '2.5rem',
      subtitleSize: isMobile ? '1.4rem' : isTablet ? '1.7rem' : '2rem',
      descriptionSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
      buttonHeight: isMobile ? '36px' : isTablet ? '38px' : '40px',
      buttonPadding: isMobile ? '6px 16px' : isTablet ? '7px 24px' : '8px 32px',
      buttonFontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
      cardPadding: isMobile ? '16px' : isTablet ? '20px' : '24px',
      statFontSize: isMobile ? '24px' : isTablet ? '28px' : '32px',
      containerPadding: isMobile ? '16px 12px' : isTablet ? '24px 16px' : '32px 16px',
      gridMinWidth: isMobile ? '200px' : '250px',
      gap: isMobile ? '16px' : '24px',
    };
  };

  const styles = getResponsiveStyles();

  // 加载本地存储的钱包数据
  useEffect(() => {
    const savedWallets = localStorage.getItem('binance-alpha-wallets');
    if (savedWallets) {
      setWallets(JSON.parse(savedWallets));
    }
  }, []);

  // 保存钱包数据到本地存储
  const saveWallets = (walletsData: WalletData[]) => {
    localStorage.setItem('binance-alpha-wallets', JSON.stringify(walletsData));
    setWallets(walletsData);
  };

  // 添加/编辑钱包
  const handleAddWallet = async (values: { address: string; remark: string }) => {
    const newWallet: WalletData = {
      id: editingWallet ? editingWallet.id : Date.now().toString(),
      address: values.address,
      remark: values.remark,
      totalVolume: editingWallet ? editingWallet.totalVolume : 0,
      transactions: editingWallet ? editingWallet.transactions : 0,
      points: editingWallet ? editingWallet.points : 0,
      earnings: editingWallet ? editingWallet.earnings : 0,
      nextLevelVolume: editingWallet ? editingWallet.nextLevelVolume : 2,
      addTime: editingWallet ? editingWallet.addTime : new Date().toISOString(),
    };

    let updatedWallets;
    if (editingWallet) {
      updatedWallets = wallets.map(w => w.id === editingWallet.id ? newWallet : w);
    } else {
      updatedWallets = [...wallets, newWallet];
    }

    saveWallets(updatedWallets);
    setIsModalVisible(false);
    setEditingWallet(null);
    form.resetFields();
    message.success(editingWallet ? '编辑钱包成功' : '添加钱包成功');
  };

  // 删除钱包
  const handleDeleteWallet = (id: string) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个钱包吗？',
      onOk: () => {
        const updatedWallets = wallets.filter(w => w.id !== id);
        saveWallets(updatedWallets);
        message.success('删除成功');
      },
    });
  };

  // 编辑钱包
  const handleEditWallet = (wallet: WalletData) => {
    setEditingWallet(wallet);
    form.setFieldsValue({
      address: wallet.address,
      remark: wallet.remark,
    });
    setIsModalVisible(true);
  };

  // 查询交易数据（真实API）
  const handleQueryTransactions = async (wallet: WalletData) => {
    setLoading(true);
    try {
      const dateString = selectedDate.format('YYYY-MM-DD');
      const apiResponse = await fetchTradingData([wallet.address], dateString);
      
      if (!apiResponse.success) {
        throw new Error('API返回失败状态');
      }
      
      const addressData = apiResponse.data[wallet.address];
      if (!addressData) {
        throw new Error('未找到该地址的交易数据');
      }
      
      // 转换数据格式，增加安全检查
      const totalVolumeUsdt = weiToUsdt(addressData.total_buy_volume_usdt);
      const gasFeeUsdt = weiToUsdt(addressData.total_gas_fees_usdt);
      
      // 计算盈亏，增加安全检查
      let totalPnl = 0;
      if (addressData.pnl_by_token && typeof addressData.pnl_by_token === 'object') {
        Object.values(addressData.pnl_by_token).forEach(pnl => {
          if (pnl) {
            totalPnl += weiToUsdt(pnl);
          }
        });
      }
      
      const points = calculatePoints(totalVolumeUsdt);
      const nextLevelVolume = getNextLevelVolume(totalVolumeUsdt);
      
      const updatedWallet: WalletData = {
        ...wallet,
        totalVolume: totalVolumeUsdt,
        transactions: addressData.trade_count,
        points,
        earnings: totalPnl - gasFeeUsdt, // 盈亏减去Gas费用
        nextLevelVolume,
      };
      
      const updatedWallets = wallets.map(w => w.id === wallet.id ? updatedWallet : w);
      saveWallets(updatedWallets);
      
      if (totalVolumeUsdt === 0 && addressData.trade_count === 0) {
        message.info(`查询完成 - 该地址在${selectedDate.format('YYYY-MM-DD')}无交易记录`);
      } else {
        message.success(`查询成功 - 交易量: $${totalVolumeUsdt.toFixed(2)}, 交易次数: ${addressData.trade_count}, 积分: ${points}`);
      }
    } catch (error) {
      console.error('查询交易数据失败:', error);
      message.error(`查询失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  // 复制地址到剪贴板
  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      message.success('地址已复制到剪贴板');
    } catch (error) {
      // 兼容较老的浏览器
      const textArea = document.createElement('textarea');
      textArea.value = address;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      message.success('地址已复制到剪贴板');
    }
  };

  // 批量查询所有钱包
  const handleBatchQuery = async () => {
    if (wallets.length === 0) {
      message.warning('请先添加钱包地址');
      return;
    }
    
    setLoading(true);
    try {
      const dateString = selectedDate.format('YYYY-MM-DD');
      const addresses = wallets.map(w => w.address);
      
      const apiResponse = await fetchTradingData(addresses, dateString);
      
      if (!apiResponse.success) {
        throw new Error('API返回失败状态');
      }
      
      // 批量更新钱包数据
      const updatedWallets = wallets.map(wallet => {
        const addressData = apiResponse.data[wallet.address];
        
        if (!addressData) {
          // 如果没有该地址的数据，保持原有数据
          return wallet;
        }
        
        // 转换数据格式，增加安全检查
        const totalVolumeUsdt = weiToUsdt(addressData.total_buy_volume_usdt);
        const gasFeeUsdt = weiToUsdt(addressData.total_gas_fees_usdt);
        
        // 计算盈亏，增加安全检查
        let totalPnl = 0;
        if (addressData.pnl_by_token && typeof addressData.pnl_by_token === 'object') {
          Object.values(addressData.pnl_by_token).forEach(pnl => {
            if (pnl) {
              totalPnl += weiToUsdt(pnl);
            }
          });
        }
        
        const points = calculatePoints(totalVolumeUsdt);
        const nextLevelVolume = getNextLevelVolume(totalVolumeUsdt);
        
        return {
          ...wallet,
          totalVolume: totalVolumeUsdt,
          transactions: addressData.trade_count,
          points,
          earnings: totalPnl - gasFeeUsdt,
          nextLevelVolume,
        };
      });
      
      saveWallets(updatedWallets);
      
      const addressesWithData = apiResponse.metadata.addresses_with_data;
      const addressesWithTrades = apiResponse.metadata.addresses_with_trades;
      const totalAddresses = apiResponse.metadata.total_addresses;
      
      if (addressesWithTrades === 0) {
        message.info(`批量查询完成 - 共查询${totalAddresses}个地址，在${selectedDate.format('YYYY-MM-DD')}均无交易记录`);
      } else {
        message.success(`批量查询完成 - ${addressesWithData}个地址有数据，${addressesWithTrades}个地址有交易`);
      }
    } catch (error) {
      console.error('批量查询失败:', error);
      message.error(`批量查询失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setLoading(false);
    }
  };

  // 计算总统计数据
  const totalStats = {
    totalTransactions: wallets.reduce((sum, w) => sum + w.transactions, 0),
    totalVolume: wallets.reduce((sum, w) => sum + w.totalVolume, 0),
    totalEarnings: wallets.reduce((sum, w) => sum + w.earnings, 0),
    totalPoints: wallets.reduce((sum, w) => sum + w.points, 0),
  };

  // 表格列定义
  const columns: ColumnsType<WalletData> = [
    {
      title: '钱包地址',
      dataIndex: 'address',
      key: 'address',
      render: (address: string) => (
        <div className="flex items-center space-x-2">
          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded-md">
            {address.slice(0, 6)}...{address.slice(-4)}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyAddress(address)}
            className="hover:bg-blue-50 hover:text-blue-600 transition-colors"
          />
        </div>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      render: (remark: string) => (
        <span className="font-medium text-gray-700">{remark}</span>
      ),
    },
    {
      title: '交易量 (USDT)',
      dataIndex: 'totalVolume',
      key: 'totalVolume',
      render: (volume: number) => (
        <div className="text-right">
          <span className="text-lg font-bold text-blue-600">
            ${volume.toFixed(2)}
          </span>
        </div>
      ),
      sorter: (a, b) => a.totalVolume - b.totalVolume,
    },
    {
      title: '交易次数',
      dataIndex: 'transactions',
      key: 'transactions',
      render: (transactions: number) => (
        <div className="text-center">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            {transactions}
          </span>
        </div>
      ),
      sorter: (a, b) => a.transactions - b.transactions,
    },
    {
      title: '积分 (2x)',
      dataIndex: 'points',
      key: 'points',
      render: (points: number) => (
        <div className="text-center">
          <Tag 
            color={points > 0 ? 'gold' : 'default'} 
            className="px-3 py-1 text-sm font-bold rounded-lg"
            icon={<TrophyOutlined />}
          >
            {points} 分
          </Tag>
        </div>
      ),
      sorter: (a, b) => a.points - b.points,
    },
    {
      title: '收益 (含Gas)',
      dataIndex: 'earnings',
      key: 'earnings',
      render: (earnings: number) => (
        <div className="text-right">
          <span className={`text-lg font-bold ${earnings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ${earnings.toFixed(2)}
          </span>
        </div>
      ),
      sorter: (a, b) => a.earnings - b.earnings,
    },
    {
      title: '下级所需',
      dataIndex: 'nextLevelVolume',
      key: 'nextLevelVolume',
      render: (volume: number, record: WalletData) => {
        const needed = volume - record.totalVolume;
        return needed > 0 ? (
          <div className="text-right">
            <span className="text-sm font-medium text-orange-600">
              还需 ${needed.toFixed(2)}
            </span>
          </div>
        ) : (
          <div className="text-center">
            <Tag color="success" className="font-bold">
              ✅ 已达标
            </Tag>
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div className="flex space-x-1">
          <Button
            type="link"
            icon={<ReloadOutlined />}
            onClick={() => handleQueryTransactions(record)}
            loading={loading}
            size="small"
            className="text-blue-600 hover:text-blue-800"
          >
            刷新
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditWallet(record)}
            size="small"
            className="text-gray-600 hover:text-gray-800"
          >
            编辑
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteWallet(record.id)}
            size="small"
            className="text-red-600 hover:text-red-800"
          >
            删除
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* 现代化Hero区域 - 优化尺寸 */}
      <div style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #1d4ed8 100%)',
        color: 'white',
        minHeight: styles.heroHeight,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.2)'
        }}></div>
        
        <div style={{
          position: 'relative',
          padding: styles.heroPadding,
          maxWidth: '1280px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: styles.titleSize,
            fontWeight: 'bold',
            marginBottom: '16px',
            lineHeight: '1.2'
          }}>
            <div>Binance Alpha</div>
            <div style={{ color: '#fde047', fontSize: styles.subtitleSize }}>积分查询工具 ⚡</div>
          </h1>
          <div style={{ marginTop: '24px', display: 'flex', gap: styles.gap, justifyContent: 'center', flexWrap: 'wrap' }}>
            <DatePicker
              value={selectedDate}
              onChange={(date) => setSelectedDate(date || dayjs())}
              format="YYYY-MM-DD"
              placeholder="选择查询日期"
              allowClear={false}
              size="large"
            />
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
              style={{
                background: '#fbbf24',
                borderColor: '#fbbf24',
                color: '#000',
                fontWeight: 'bold',
                padding: styles.buttonPadding,
                height: styles.buttonHeight
              }}
            >
              添加钱包
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<ReloadOutlined />}
              onClick={handleBatchQuery}
              loading={loading}
              style={{
                background: '#059669',
                borderColor: '#059669',
                fontWeight: 'bold',
                padding: styles.buttonPadding,
                height: styles.buttonHeight
              }}
            >
              批量查询
            </Button>
          </div>
        </div>
      </div>

      {/* 统计卡片区域 */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: styles.containerPadding }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: `repeat(auto-fit, minmax(${styles.gridMinWidth}, 1fr))`, 
          gap: styles.gap, 
          marginBottom: responsive.isMobile ? '24px' : '32px' 
        }}>
          {/* 总交易次数 */}
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            borderRadius: '16px',
            padding: styles.cardPadding,
            color: 'white',
            boxShadow: '0 10px 25px rgba(59, 130, 246, 0.3)',
            transform: 'translateY(0)',
            transition: 'transform 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                padding: responsive.isMobile ? '8px' : '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: responsive.isMobile ? '8px' : '12px',
                marginRight: responsive.isMobile ? '12px' : '16px'
              }}>
                <SwapOutlined style={{ fontSize: responsive.isMobile ? '20px' : '24px' }} />
              </div>
              <div>
                <p style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: responsive.isMobile ? '12px' : '14px', 
                  opacity: 0.8 
                }}>总交易次数</p>
                <p style={{ margin: 0, fontSize: styles.statFontSize, fontWeight: 'bold' }}>{totalStats.totalTransactions}</p>
              </div>
            </div>
          </div>

          {/* 总交易额 */}
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '16px',
            padding: styles.cardPadding,
            color: 'white',
            boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
            transform: 'translateY(0)',
            transition: 'transform 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                padding: responsive.isMobile ? '8px' : '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: responsive.isMobile ? '8px' : '12px',
                marginRight: responsive.isMobile ? '12px' : '16px'
              }}>
                <DollarOutlined style={{ fontSize: responsive.isMobile ? '20px' : '24px' }} />
              </div>
              <div>
                <p style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: responsive.isMobile ? '12px' : '14px', 
                  opacity: 0.8 
                }}>总交易额</p>
                <p style={{ margin: 0, fontSize: styles.statFontSize, fontWeight: 'bold' }}>${totalStats.totalVolume.toFixed(0)}</p>
              </div>
            </div>
          </div>

          {/* 总积分 */}
          <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            borderRadius: '16px',
            padding: styles.cardPadding,
            color: 'white',
            boxShadow: '0 10px 25px rgba(139, 92, 246, 0.3)',
            transform: 'translateY(0)',
            transition: 'transform 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                padding: responsive.isMobile ? '8px' : '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: responsive.isMobile ? '8px' : '12px',
                marginRight: responsive.isMobile ? '12px' : '16px'
              }}>
                <TrophyOutlined style={{ fontSize: responsive.isMobile ? '20px' : '24px' }} />
              </div>
              <div>
                <p style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: responsive.isMobile ? '12px' : '14px', 
                  opacity: 0.8 
                }}>总积分</p>
                <p style={{ margin: 0, fontSize: styles.statFontSize, fontWeight: 'bold' }}>{totalStats.totalPoints}</p>
              </div>
            </div>
          </div>

          {/* 总收益 */}
          <div style={{
            background: totalStats.totalEarnings >= 0 
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: '16px',
            padding: styles.cardPadding,
            color: 'white',
            boxShadow: totalStats.totalEarnings >= 0 
              ? '0 10px 25px rgba(16, 185, 129, 0.3)'
              : '0 10px 25px rgba(239, 68, 68, 0.3)',
            transform: 'translateY(0)',
            transition: 'transform 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                padding: responsive.isMobile ? '8px' : '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: responsive.isMobile ? '8px' : '12px',
                marginRight: responsive.isMobile ? '12px' : '16px'
              }}>
                <StarOutlined style={{ fontSize: responsive.isMobile ? '20px' : '24px' }} />
              </div>
              <div>
                <p style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: responsive.isMobile ? '12px' : '14px', 
                  opacity: 0.8 
                }}>总收益</p>
                <p style={{ margin: 0, fontSize: styles.statFontSize, fontWeight: 'bold' }}>${totalStats.totalEarnings.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 钱包表格区域 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: responsive.isMobile ? '12px' : '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: styles.cardPadding,
            background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: responsive.isMobile ? 'flex-start' : 'center',
              flexDirection: responsive.isMobile ? 'column' : 'row',
              gap: responsive.isMobile ? '8px' : '0'
            }}>
              <div>
                <h3 style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: responsive.isMobile ? '20px' : '24px', 
                  fontWeight: 'bold', 
                  color: '#111827' 
                }}>钱包管理</h3>
                <p style={{ 
                  margin: 0, 
                  color: '#6b7280',
                  fontSize: responsive.isMobile ? '13px' : '14px'
                }}>管理您的钱包地址并查看详细数据</p>
              </div>
              <div style={{ 
                fontSize: responsive.isMobile ? '13px' : '14px', 
                color: '#6b7280' 
              }}>
                共 {wallets.length} 个钱包
              </div>
            </div>
          </div>
          
          <div style={{ padding: styles.cardPadding }}>
            {wallets.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: responsive.isMobile ? '32px 0' : '64px 0' 
              }}>
                <div style={{
                  width: responsive.isMobile ? '72px' : '96px',
                  height: responsive.isMobile ? '72px' : '96px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto'
                }}>
                  <PlusOutlined style={{ 
                    fontSize: responsive.isMobile ? '36px' : '48px', 
                    color: '#9ca3af' 
                  }} />
                </div>
                <h3 style={{ 
                  fontSize: responsive.isMobile ? '18px' : '20px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>暂无钱包数据</h3>
                <p style={{ 
                  color: '#6b7280', 
                  marginBottom: responsive.isMobile ? '20px' : '24px',
                  fontSize: responsive.isMobile ? '14px' : '16px'
                }}>添加您的第一个钱包地址开始查询积分数据</p>
                <Button
                  type="primary" 
                  size={responsive.isMobile ? 'middle' : 'large'}
                  icon={<PlusOutlined />}
                  onClick={() => setIsModalVisible(true)}
                  style={{
                    background: '#3b82f6',
                    borderColor: '#3b82f6',
                    padding: responsive.isMobile ? '6px 24px' : '8px 32px',
                    height: responsive.isMobile ? '40px' : '48px',
                    fontSize: responsive.isMobile ? '16px' : '18px',
                    fontWeight: '500'
                  }}
                >
                  添加钱包地址
                </Button>
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={wallets}
                rowKey="id"
                scroll={{ x: responsive.isMobile ? 800 : undefined }}
                size={responsive.isMobile ? 'small' : 'middle'}
                pagination={{
                  showSizeChanger: !responsive.isMobile,
                  showQuickJumper: !responsive.isMobile,
                  showTotal: (total, range) => 
                    responsive.isMobile 
                      ? `${range[0]}-${range[1]} / ${total}`
                      : `第 ${range[0]}-${range[1]} 条，共 ${total} 个钱包`,
                  pageSize: responsive.isMobile ? 5 : 10,
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* 添加/编辑钱包模态框 */}
      <Modal
        title={
          <div style={{ 
            fontSize: responsive.isMobile ? '18px' : '20px', 
            fontWeight: 'bold', 
            color: '#374151' 
          }}>
            {editingWallet ? '✏️ 编辑钱包' : '➕ 添加钱包'}
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingWallet(null);
          form.resetFields();
        }}
        footer={null}
        width={responsive.isMobile ? '90%' : 520}
        style={{ top: responsive.isMobile ? 20 : 100 }}
      >
        <div style={{ padding: responsive.isMobile ? '16px 0' : '24px 0' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddWallet}
          >
            <Form.Item
              label={
                <span style={{ 
                  fontSize: responsive.isMobile ? '14px' : '15px', 
                  fontWeight: '500', 
                  color: '#374151' 
                }}>
                  钱包地址
                </span>
              }
              name="address"
              rules={[
                { required: true, message: '请输入钱包地址' },
                { min: 20, message: '钱包地址长度不正确' },
              ]}
            >
              <Input 
                placeholder="请输入钱包地址" 
                style={{ 
                  height: responsive.isMobile ? '40px' : '48px',
                  fontSize: responsive.isMobile ? '14px' : '16px'
                }}
              />
            </Form.Item>
            
            <Form.Item
              label={
                <span style={{ 
                  fontSize: responsive.isMobile ? '14px' : '15px', 
                  fontWeight: '500', 
                  color: '#374151' 
                }}>
                  备注信息
                </span>
              }
              name="remark"
              rules={[{ required: false, message: '请输入备注' }]}
            >
              <Input 
                placeholder="请输入备注信息，如：主钱包、测试钱包等" 
                style={{ 
                  height: responsive.isMobile ? '40px' : '48px',
                  fontSize: responsive.isMobile ? '14px' : '16px'
                }}
              />
            </Form.Item>
            
            <Form.Item style={{ marginBottom: 0, paddingTop: responsive.isMobile ? '16px' : '24px' }}>
              <div style={{ 
                display: 'flex', 
                gap: responsive.isMobile ? '12px' : '16px',
                flexDirection: responsive.isMobile ? 'column' : 'row'
              }}>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  size={responsive.isMobile ? 'middle' : 'large'}
                  style={{
                    flex: 1,
                    height: responsive.isMobile ? '40px' : '48px',
                    fontSize: responsive.isMobile ? '16px' : '18px',
                    fontWeight: '500',
                    background: '#3b82f6',
                    borderColor: '#3b82f6'
                  }}
                >
                  {editingWallet ? '💾 更新钱包' : '✨ 添加钱包'}
                </Button>
                <Button
                  size={responsive.isMobile ? 'middle' : 'large'}
                  style={{
                    height: responsive.isMobile ? '40px' : '48px',
                    fontSize: responsive.isMobile ? '16px' : '18px',
                    minWidth: responsive.isMobile ? 'auto' : '120px'
                  }}
                  onClick={() => {
                    setIsModalVisible(false);
                    setEditingWallet(null);
                    form.resetFields();
                  }}
                >
                  取消
                </Button>
              </div>
            </Form.Item>
          </Form>
        </div>
      </Modal>

      {/* 交易详情模态框 */}
      <Modal
        title={
          <span style={{ 
            fontSize: responsive.isMobile ? '16px' : '18px', 
            fontWeight: 'bold' 
          }}>
            交易详情
          </span>
        }
        open={transactionDetailsVisible}
        onCancel={() => setTransactionDetailsVisible(false)}
        footer={null}
        width={responsive.isMobile ? '95%' : 1200}
        style={{ top: responsive.isMobile ? 20 : 50 }}
      >
        <Table
          columns={[
            {
              title: '交易哈希',
              dataIndex: 'tx_hash',
              key: 'tx_hash',
              width: responsive.isMobile ? 120 : 150,  
              render: (hash: string) => (
                <a
                  href={`https://bscscan.com/tx/${hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#3b82f6',
                    fontFamily: 'monospace',
                    fontSize: responsive.isMobile ? '12px' : '14px'
                  }}
                >
                  {hash.slice(0, responsive.isMobile ? 8 : 10)}...{hash.slice(-6)}
                </a>
              ),
            },
            {
              title: '类型',
              dataIndex: 'trade_type',
              key: 'trade_type',
              width: responsive.isMobile ? 60 : 80,
              render: (type: string) => (
                <Tag 
                  color={type === 'buy' ? 'green' : 'red'} 
                  style={{ fontSize: responsive.isMobile ? '11px' : '12px' }}
                >
                  {type === 'buy' ? '买入' : '卖出'}
                </Tag>
              ),
            },
            ...(!responsive.isMobile ? [
              {
                title: '从代币数量',
                dataIndex: 'from_amount',
                key: 'from_amount',
                render: (amount: string) => weiToUsdt(amount).toFixed(6),
              },
              {
                title: '到代币数量', 
                dataIndex: 'to_amount',
                key: 'to_amount',
                render: (amount: string) => weiToUsdt(amount).toFixed(6),
              },
            ] : []),
            {
              title: 'Gas费用',
              dataIndex: 'gas_fee',
              key: 'gas_fee',
              width: responsive.isMobile ? 100 : 120,
              render: (fee: string) => (
                <span style={{ fontSize: responsive.isMobile ? '11px' : '12px' }}>
                  {weiToUsdt(fee).toFixed(responsive.isMobile ? 6 : 8)} BNB
                </span>
              ),
            },
            {
              title: '时间',
              dataIndex: 'timestamp',
              key: 'timestamp',
              width: responsive.isMobile ? 120 : 160,
              render: (timestamp: number) => (
                <span style={{ fontSize: responsive.isMobile ? '11px' : '12px' }}>
                  {new Date(timestamp * 1000).toLocaleString('zh-CN', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              ),
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: responsive.isMobile ? 60 : 80,
              render: (status: number) => (
                <Tag 
                  color={status === 1 ? 'green' : 'red'}
                  style={{ fontSize: responsive.isMobile ? '11px' : '12px' }}
                >
                  {status === 1 ? '成功' : '失败'}
                </Tag>
              ),
            },
          ]}
          dataSource={selectedWalletTransactions}
          rowKey="tx_hash"
          scroll={{ x: responsive.isMobile ? 600 : undefined }}
          pagination={{
            pageSize: responsive.isMobile ? 5 : 10,
            showSizeChanger: !responsive.isMobile,
            showQuickJumper: !responsive.isMobile,
            showTotal: (total) => 
              responsive.isMobile 
                ? `共 ${total} 笔`
                : `共 ${total} 笔交易`,
          }}
          size={responsive.isMobile ? 'small' : 'middle'}
        />
      </Modal>
    </>
  );
};

export default BinanceAlpha; 